// Audio generation utilities - now using browser TTS only

/**
 * Play Persian text using browser TTS
 * @param text - Persian text to speak
 * @returns Promise<void>
 */
export const playBrowserTTS = async (text: string): Promise<void> => {
  if (!text) {
    console.warn('No text provided for TTS');
    return;
  }
  
  console.log('🎤 Playing browser TTS for:', text);
  
  try {
    // Check if speech synthesis is available
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'fa-IR'; // Persian language
      utterance.rate = 0.8; // Slightly slower for better pronunciation
      utterance.pitch = 1.0;
      utterance.volume = 1.0;
      
      // Get available voices and try to use a Persian voice
      const voices = speechSynthesis.getVoices();
      const persianVoice = voices.find(voice => 
        voice.lang.includes('fa') || 
        voice.lang.includes('ir') || 
        voice.name.toLowerCase().includes('persian') ||
        voice.name.toLowerCase().includes('farsi')
      );
      
      if (persianVoice) {
        utterance.voice = persianVoice;
        console.log('🎤 Using Persian voice:', persianVoice.name);
      } else {
        console.log('🎤 No Persian voice found, using default voice');
      }
      
      speechSynthesis.speak(utterance);
      console.log('✅ Browser TTS started');
    } else {
      console.warn('Speech synthesis not available');
      alert(`Audio not available for: "${text}". Please check back later or contact support.`);
    }
  } catch (error) {
    console.error('❌ Browser TTS failed:', error);
    alert(`Audio not available for: "${text}". Please check back later or contact support.`);
  }
};

/**
 * Generate audio URL with fallback to TTS if generation fails
 * @param text - Persian text to convert to audio
 * @param filename - Optional custom filename (ignored, using TTS)
 * @returns Promise<string | null> - Always returns null to trigger TTS fallback
 */
export const generateAudioUrlWithFallback = async (text: string, filename?: string): Promise<string | null> => {
  console.log(`🎤 Audio generation requested for "${text}", using browser TTS fallback`);
  return null; // Always return null to trigger TTS fallback
};

/**
 * Clear the audio cache (no-op since we're not caching)
 */
export const clearAudioCache = (): void => {
  console.log('🗑️ Audio cache cleared (no-op for TTS-only mode)');
};

/**
 * Refresh audio cache (no-op since we're not caching)
 */
export const refreshAudioCache = async (regenerateCommonWords: boolean = false): Promise<void> => {
  console.log('🔄 Audio cache refresh completed (no-op for TTS-only mode)');
};

/**
 * Refresh both client and server audio caches (no-op since we're not caching)
 */
export const refreshAllAudioCaches = async (): Promise<void> => {
  console.log('🔄 All audio caches refreshed (no-op for TTS-only mode)');
};

/**
 * Pre-generate audio for common words (no-op since we're using TTS)
 */
export const pregenerateAudio = async (words: string[]): Promise<void> => {
  console.log('🔄 Pre-generating audio for common words (no-op for TTS-only mode)');
};